import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/hooks/useAuth';
import type { OrderWithDetails, MenuItem, Order, OrderItemWithDetails } from '@/types/common';

interface UseOrderOperationsProps {
  order: OrderWithDetails | null;
  onOrderUpdate: (updatedOrder: OrderWithDetails) => Promise<void>;
}

export function useOrderOperations({ order, onOrderUpdate }: UseOrderOperationsProps) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const addOrderItem = useCallback(async (item: MenuItem, selectedTable: string) => {
    if (saving) return;
    if (!selectedTable) {
      addToast({
        type: 'error',
        message: 'Please select a table first'
      });
      return;
    }

    try {
      setSaving(true);
      
      // Create order if it doesn't exist
      if (!order) {
        if (!user) {
          throw new Error('You must be logged in to create an order');
        }

        // Check if table is available (only for new orders)
        const { data: tableData, error: tableError } = await supabase
          .from('tables')
          .select('status')
          .eq('id', selectedTable)
          .single();

        if (tableError) throw tableError;
        if (tableData.status !== 'available') {
          throw new Error('Selected table is not available');
        }

        // Check if table already has an active order
        const { data: existingOrders, error: orderError } = await supabase
          .from('orders')
          .select('id')
          .eq('table_id', selectedTable)
          .in('status', ['pending', 'preparing', 'ready', 'delivered'])
          .limit(1);

        if (orderError) throw orderError;
        if (existingOrders && existingOrders.length > 0) {
          throw new Error('Selected table already has an active order');
        }

        const { data: newOrder, error: orderError2 } = await supabase
          .from('orders')
          .insert({
            table_id: selectedTable,
            status: 'pending',
            total: item.price,
            created_by: user.id,
          })
          .select('*, table:tables(*)')
          .single();

        if (orderError2) throw orderError2;
        
        const { data, error: itemError } = await supabase
          .from('order_items')
          .insert({
            order_id: newOrder.id,
            menu_item_id: item.id,
            quantity: 1,
          })
          .select('*, menu_item:menu_items(*)')
          .single();

        if (itemError) throw itemError;
        
        addToast({
          type: 'success',
          message: `Added ${item.name} to order`,
        });

        // Update locally before navigation
        const orderWithItems: OrderWithDetails = {
          ...newOrder,
          items: [data],
          total: newOrder.total
        };
        
        await onOrderUpdate(orderWithItems);
        
        // Navigate after state is updated
        navigate(`/orders/${newOrder.id}`);
      } else {
        // Add item to existing order
        const existingItem = order.items.find(i => i.menu_item_id === item.id);
        const newQuantity = existingItem ? existingItem.quantity + 1 : 1;
        const newTotal = order.total + item.price;

        let updatedItem;
        if (existingItem) {
          // Update quantity locally first
          updatedItem = {
            ...existingItem,
            quantity: newQuantity
          };

          // Then update in database
          const { error } = await supabase
            .from('order_items')
            .update({ quantity: newQuantity })
            .eq('id', existingItem.id);

          if (error) throw error;
        } else {
          // Insert new item
          const { data, error } = await supabase
            .from('order_items')
            .insert({
              order_id: order.id,
              menu_item_id: item.id,
              quantity: 1
            })
            .select('*, menu_item:menu_items(*)')
            .single();

          if (error) throw error;
          updatedItem = data;
        }

        // Update order total
        const { error: updateError } = await supabase
          .from('orders')
          .update({ 
            total: newTotal,
            updated_at: new Date().toISOString()
          })
          .match({ id: order.id });

        if (updateError) throw updateError;

        // Update local state immediately
        const updatedOrder = {
          ...order,
          items: existingItem 
            ? order.items.map(i => i.id === existingItem.id ? updatedItem : i)
            : [...order.items, updatedItem],
          total: newTotal
        };

        await onOrderUpdate(updatedOrder);

        addToast({
          type: 'success',
          message: `Added ${item.name} to order`,
        });
      }
    } catch (error) {
      console.error('Error adding item:', error);
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to add item to order',
      });
    } finally {
      setSaving(false);
    }
  }, [saving, order, user, navigate, addToast, onOrderUpdate]);

  const updateItemQuantity = async (item: OrderItemWithDetails, newQuantity: number) => {
    if (!order) throw new Error('No active order');
    
    // Find the item in the order
    const orderItem = order.items.find(i => i.id === item.id);
    if (!orderItem) throw new Error('Item not found');

    try {
      setSaving(true);

      if (newQuantity === 0) {
        // Delete the item if quantity is 0
        const { error } = await supabase
          .from('order_items')
          .delete()
          .eq('id', item.id);

        if (error) throw error;

        // Update local state
        const updatedOrder = {
          ...order,
          items: order.items.filter(i => i.id !== item.id),
          total: order.total - (item.quantity * item.menu_item.price)
        };

        await onOrderUpdate(updatedOrder);
      } else {
        // Update quantity if greater than 0
        const { data, error } = await supabase
          .from('order_items')
          .update({ quantity: newQuantity })
          .eq('id', item.id)
          .select('*')
          .single();

        if (error) throw error;

        // Update local state
        const updatedOrder = {
          ...order,
          items: order.items.map(i => 
            i.id === item.id 
              ? { ...i, quantity: newQuantity }
              : i
          ),
          total: order.total + ((newQuantity - item.quantity) * item.menu_item.price)
        };

        await onOrderUpdate(updatedOrder);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    addOrderItem,
    updateItemQuantity
  };
}